import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const SWIPE_THRESHOLD_RATIO = 0.2;
const RESUME_DELAY = 4000;

/**
 * Touch/drag-enabled video carousel with partial-peek slides, lazy video
 * loading (only the active slide and its immediate neighbors ever load
 * beyond `preload="none"`), and an optional per-slide shoppable product tag.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} slides
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement[]} [productTriggers]
 * @property {HTMLElement[]} [productCards]
 *
 * @extends {Component<Refs>}
 */
export class VideoCarousel extends Component {
  requiredRefs = ['track', 'slides'];

  /** @type {number | undefined} */
  #interval;
  /** @type {number | undefined} */
  #resumeTimer;
  #hovering = false;
  #current = 0;

  #dragging = false;
  #dragStartX = 0;
  #dragBaseOffset = 0;
  #dragDelta = 0;
  /** @type {number | null} */
  #dragPointerId = null;

  #openProductIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#loadVideosAround(0);
    this.#updatePosition(false);
    this.play();

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    document.addEventListener('click', this.#handleDocumentClick);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    clearTimeout(this.#resumeTimer);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    document.removeEventListener('click', this.#handleDocumentClick);
    window.removeEventListener('resize', this.#handleResize);
  }

  get track() {
    return this.refs.track;
  }

  get slides() {
    return this.refs.slides ?? [];
  }

  get loop() {
    return this.hasAttribute('loop') && this.slides.length > 1;
  }

  get autoplayInterval() {
    const raw = this.getAttribute('autoplay');
    if (raw === null) return undefined;

    const seconds = parseFloat(raw);
    return Number.isNaN(seconds) ? undefined : seconds * 1000;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval) && this.slides.length > 1 && !prefersReducedMotion();
  }

  play() {
    if (this.#interval || !this.autoplay) return;
    this.#interval = window.setInterval(() => this.next(), this.autoplayInterval);
  }

  suspend() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault();
    this.#interact();

    const atEnd = this.#current >= this.slides.length - 1;
    if (atEnd && !this.loop) return;

    this.#goTo(atEnd ? 0 : this.#current + 1);
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault();
    this.#interact();

    const atStart = this.#current <= 0;
    if (atStart && !this.loop) return;

    this.#goTo(atStart ? this.slides.length - 1 : this.#current - 1);
  }

  /**
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    this.#interact();

    const target = Number(index);
    if (Number.isNaN(target) || target === this.#current) return;

    this.#goTo(target);
  }

  handlePointerEnter() {
    this.#hovering = true;
    this.suspend();
  }

  handlePointerLeave() {
    this.#hovering = false;
    clearTimeout(this.#resumeTimer);
    this.play();
  }

  /** @param {Event} event */
  handleProductTriggerClick(event) {
    event.preventDefault();

    const trigger = /** @type {HTMLButtonElement} */ (event.target);
    const index = (this.refs.productTriggers ?? []).indexOf(trigger);
    if (index === -1) return;

    this.#setOpenProduct(index === this.#openProductIndex ? -1 : index);
  }

  #interact() {
    this.suspend();
    clearTimeout(this.#resumeTimer);

    this.#resumeTimer = window.setTimeout(() => {
      if (!this.#hovering) this.play();
    }, RESUME_DELAY);
  }

  /** @param {number} index */
  #goTo(index) {
    this.#current = index;
    this.#loadVideosAround(index);
    this.#updateActiveState();
    this.#updatePosition(true);
    this.#setOpenProduct(-1);
  }

  #updateActiveState() {
    this.slides.forEach((slide, i) => {
      const active = i === this.#current;
      const video = slide.querySelector('video');
      if (!video) return;

      if (active) {
        if (video.loop && video.muted && !prefersReducedMotion()) {
          video.play().catch(() => {});
        }
      } else {
        video.pause();
      }
    });

    (this.refs.dots ?? []).forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === this.#current));
    });
  }

  /** @param {boolean} animate */
  #updatePosition(animate) {
    const track = this.track;
    if (!track) return;

    track.style.transition = animate && !prefersReducedMotion() ? 'transform 0.35s ease' : 'none';
    track.style.transform = `translateX(-${this.#offsetForIndex(this.#current)}px)`;
  }

  /**
   * Loads video metadata for the active slide plus its immediate neighbors;
   * every other slide stays at `preload="none"` so nothing downloads until
   * the user approaches it.
   * @param {number} index
   */
  #loadVideosAround(index) {
    const range = new Set([index - 1, index, index + 1]);

    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;

      if (range.has(i)) {
        if (video.preload !== 'metadata' && video.preload !== 'auto') {
          video.preload = 'metadata';
          video.load();
        }
      }
    });
  }

  /** @param {number} index */
  #setOpenProduct(index) {
    this.#openProductIndex = index;

    (this.refs.productTriggers ?? []).forEach((trigger, i) => {
      const open = i === index;
      trigger.setAttribute('aria-expanded', String(open));
      trigger.closest('.video-carousel__product')?.setAttribute('data-open', String(open));
    });
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (this.#openProductIndex === -1) return;

    const openTrigger = (this.refs.productTriggers ?? [])[this.#openProductIndex];
    const openContainer = openTrigger?.closest('.video-carousel__product');
    if (openContainer && event.composedPath().includes(openContainer)) return;

    this.#setOpenProduct(-1);
  };

  #handleResize = () => {
    this.#updatePosition(false);
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#dragging = true;
    this.#dragStartX = event.clientX;
    this.#dragDelta = 0;
    this.#dragPointerId = event.pointerId;
    this.#dragBaseOffset = this.#offsetForIndex(this.#current);
    this.suspend();
    this.track?.style.setProperty('transition', 'none');
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#dragPointerId) return;

    this.#dragDelta = event.clientX - this.#dragStartX;
    const track = this.track;
    if (track) track.style.transform = `translateX(${-(this.#dragBaseOffset - this.#dragDelta)}px)`;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#dragPointerId) return;

    this.#dragging = false;
    this.#dragPointerId = null;

    const activeSlide = this.slides[this.#current];
    const slideWidth = activeSlide?.offsetWidth ?? 0;
    const threshold = slideWidth * SWIPE_THRESHOLD_RATIO;

    if (this.#dragDelta <= -threshold) {
      this.next();
    } else if (this.#dragDelta >= threshold) {
      this.previous();
    } else {
      this.#interact();
      this.#updatePosition(true);
    }

    this.#dragDelta = 0;
  };

  /** @param {number} index */
  #offsetForIndex(index) {
    const track = this.track;
    if (!track) return 0;

    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    let offset = 0;
    for (let i = 0; i < index; i++) {
      const slide = this.slides[i];
      if (slide) offset += slide.offsetWidth + gap;
    }
    return offset;
  }
}

if (!customElements.get('video-carousel-component')) {
  customElements.define('video-carousel-component', VideoCarousel);
}
